// Technical Alerts Theme System
// Centralized styles and constants for all Technical Alerts components

// Font families
export const fontFamily = {
  primary: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
  display: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
  text: '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif'
};

// Typography scale
export const typography = {
  // Headers
  h1: {
    fontSize: '20px',
    fontWeight: 700,
    lineHeight: '24px',
    fontFamily: fontFamily.display
  },
  h2: {
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: '22px',
    fontFamily: fontFamily.display
  },
  h3: {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '20px',
    fontFamily: fontFamily.display
  },
  h4: {
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: '20px',
    fontFamily: fontFamily.text
  },
  h5: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '18px',
    fontFamily: fontFamily.text
  },
  
  // Body text
  body1: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '18px',
    fontFamily: fontFamily.text
  },
  body2: {
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '16px',
    fontFamily: fontFamily.text
  },
  body3: {
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '16px',
    fontFamily: fontFamily.text
  },
  
  // Small text
  caption: {
    fontSize: '11px',
    fontWeight: 500,
    lineHeight: '14px',
    fontFamily: fontFamily.text
  },
  micro: {
    fontSize: '10px',
    fontWeight: 500,
    lineHeight: '12px',
    fontFamily: fontFamily.text
  },
  tiny: {
    fontSize: '9px',
    fontWeight: 600,
    lineHeight: '10px',
    fontFamily: fontFamily.text
  },
  smallest: {
    fontSize: '8px',
    fontWeight: 500,
    lineHeight: '10px',
    fontFamily: fontFamily.text
  }
};

// Color system
export const colors = {
  // Primary palette
  primary: '#007AFF',
  primaryHover: 'rgba(0, 122, 255, 0.8)',
  primaryBackground: 'rgba(0, 122, 255, 0.08)',
  primaryBorder: 'rgba(0, 122, 255, 0.2)',
  
  // Alert colors
  alert: '#E07B67',
  alertHover: '#D96B55',
  alertBackground: 'rgba(224, 123, 103, 0.08)',
  alertLight: 'rgba(224, 123, 103, 0.02)',
  alertBorder: 'rgba(224, 123, 103, 0.3)',
  
  // Status colors
  success: '#8BC38F',
  successBackground: 'rgba(139, 195, 143, 0.08)',
  warning: '#F59E0B',
  warningBackground: 'rgba(245, 158, 11, 0.08)',
  error: '#EF4444',
  errorBackground: 'rgba(239, 68, 68, 0.08)',
  
  // Neutral colors
  text: {
    primary: '#000',
    secondary: '#37352f',
    tertiary: '#4A4037',
    muted: '#787774',
    light: '#A08B7A',
    subtle: 'rgba(0, 0, 0, 0.6)',
    hint: 'rgba(0, 0, 0, 0.5)',
    disabled: '#cccccc'
  },
  
  // Background colors
  background: {
    white: '#ffffff',
    light: '#F9F7F4',
    card: '#ffffff',
    hover: 'rgba(0, 0, 0, 0.02)',
    selected: 'rgba(0, 122, 255, 0.08)'
  },
  
  // Border colors
  border: {
    light: 'rgba(0, 0, 0, 0.06)',
    normal: 'rgba(0, 0, 0, 0.08)',
    medium: 'rgba(0, 0, 0, 0.1)',
    dark: 'rgba(0, 0, 0, 0.15)',
    focus: '#C4A484'
  }
};

// Spacing system
export const spacing = {
  xs: '4px',
  sm: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  '2xl': '20px',
  '3xl': '24px',
  '4xl': '32px'
};

// Border radius
export const borderRadius = {
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '10px',
  '2xl': '12px',
  round: '50%'
};

// Shadows
export const shadows = {
  sm: '0px 1px 3px rgba(0, 0, 0, 0.04)',
  md: '0px 2px 8px rgba(0, 0, 0, 0.08)',
  lg: '0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 0.5px rgba(0, 0, 0, 0.05)',
  xl: '0 10px 40px rgba(0, 0, 0, 0.12)',
  focus: '0px 0px 0px 2px #C4A484'
};

// Animation/Transition timing
export const transitions = {
  fast: '0.1s ease',
  normal: '0.2s ease',
  slow: '0.3s ease',
  bezier: '0.2s cubic-bezier(0.4, 0.0, 0.2, 1)'
};

// Z-index scale
export const zIndex = {
  dropdown: 1000,
  popover: 1300,
  modal: 1400,
  tooltip: 1500
};

// Common style objects
export const commonStyles = {
  // Flex layouts
  flexCenter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  flexBetween: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column'
  },
  
  // Common text styles
  ellipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  
  // Interactive elements
  clickable: {
    cursor: 'pointer',
    transition: transitions.normal
  },
  
  // Card styles
  card: {
    backgroundColor: colors.background.card,
    borderRadius: borderRadius.xl,
    border: `1px solid ${colors.border.normal}`,
    boxShadow: shadows.sm,
    transition: transitions.normal,
    '&:hover': {
      boxShadow: shadows.md,
      borderColor: colors.border.dark
    }
  }
};

const themeConstants = {
  fontFamily,
  typography,
  colors,
  spacing,
  borderRadius,
  shadows,
  transitions,
  zIndex,
  commonStyles
};

export default themeConstants;